import { ChoiceList, Icon, Spinner, TextField } from "@shopify/polaris";
import { SearchMinor } from "@shopify/polaris-icons";
import isEqual from "lodash/isEqual";
import React from "react";
import styled from "styled-components";
import { handleError, reducerFn } from "../../helper";
import { useCallbackRef } from "../../hooks/useCallbackRef";

const ALL = { value: "All", label: "All" };
function FilterSingleValue({
  options,
  value,
  loading,
  error,
  placeholder,
  multiple,
  noAll = false,
  noSearch = false,
  onSearchChange,
  onChange: onChangeProp,
}) {
  const [state, setState] = React.useReducer(reducerFn, {
    inputVal: "",
    options: options,
    deselectedOptions: options,
    selected: value,
  });
  const onChange = useCallbackRef(onChangeProp);

  // Effect
  const optionsStr = JSON.stringify(options);
  const hasSearch = React.useRef(false);
  React.useEffect(() => {
    let options = JSON.parse(optionsStr) || [];

    if (!hasSearch.current && !noAll) {
      options.unshift(ALL);
    }
    setState({ deselectedOptions: options, options });
  }, [optionsStr, noAll]);

  const valuePrev = React.useRef(value);
  React.useEffect(() => {
    if (!isEqual(value, valuePrev.current)) {
      setState({ selected: value });
      valuePrev.current = value;
    }
  }, [value]);

  const typingRef = React.useRef(null);
  const handleInputChange = React.useCallback(
    (value, id) => {
      setState({ [id]: value });
      hasSearch.current = !!value;

      typingRef.current && clearTimeout(typingRef.current);
      typingRef.current = setTimeout(() => {
        onSearchChange
          ? onSearchChange(value)
          : (() => {
              if (!value) {
                setState({ options: state.deselectedOptions });
                return;
              }

              const pt = new RegExp(value, "i");
              const newOptions = state.deselectedOptions.filter((opt) =>
                opt.label.match(pt)
              );
              setState({ options: newOptions });
            })();
      }, 500);
    },
    [state.deselectedOptions, onSearchChange]
  );

  const handleInputRemove = React.useCallback(() => {
    handleInputChange("", "inputVal");
  }, [handleInputChange]);

  const handleSelection = React.useCallback(
    (selected) => {
      setState({ selected });
      const selectedOptions = state.deselectedOptions.filter(({ value }) =>
        selected.includes(value)
      );
      onChange && onChange(selectedOptions);
    },
    [state.deselectedOptions, onChange]
  );

  React.useEffect(
    () => () => {
      clearTimeout(typingRef.current);
    },
    []
  );

  return (
    <Wrapper>
      {noSearch ? null : (
        <TextField
          value={state.inputVal}
          onChange={handleInputChange}
          id="inputVal"
          prefix={<Icon source={SearchMinor} color="inkLighter" />}
          autoFocus
          placeholder={placeholder || "Filter item"}
          onClearButtonClick={handleInputRemove}
          clearButton
        />
      )}
      <div className="mt-4">
        {loading ? (
          <Spinner size="small" />
        ) : error ? (
          <div>Error: {handleError(error.toString())}</div>
        ) : state.options?.length > 0 ? (
          <div className="choicelist-wrap">
            <ChoiceList
              choices={state.options}
              selected={state.selected}
              onChange={handleSelection}
              allowMultiple={multiple}
            />
          </div>
        ) : (
          <span>No items found</span>
        )}
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .choicelist-wrap {
    margin-top: 1rem;
  }

  .Polaris-ChoiceList__Choices {
    max-height: 280px;
    overflow-y: scroll;
    padding-left: 2px;
  }
`;

export default FilterSingleValue;
